import { useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import { getSupabase } from '@/lib/supabase';
import { useFeatureFlags } from '@/modules/admin/useFeatureFlags';
import { deriveAgeStatus, canAccessGatedSurfaces, type AgeStatus } from './ageGate';

/**
 * Hard age-gate state + enforcement decision.
 *
 * Enforcement is ON only when the `age_gate` feature flag is enabled AND the
 * backing table is provisioned AND there's a backend. Otherwise it degrades
 * to `canAccess: true` (no enforcement) — so shipping the gate to an
 * environment without the migration applied, or with the flag off, never
 * bricks the app. When enforced, only a 'verified' age-check passes.
 */
export const useAgeGate = () => {
  const { user } = useAuth();
  const { isEnabled, loading: flagsLoading } = useFeatureFlags();
  const [status, setStatus] = useState<AgeStatus>('unverified');
  const [loading, setLoading] = useState(true);
  const [provisioned, setProvisioned] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const supabase = getSupabase();
    if (!supabase || !user) {
      setLoading(false);
      return;
    }
    supabase
      .from('hottr_age_checks')
      .select('status')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setProvisioned(false); // table likely absent pre-migration
        } else {
          setStatus(deriveAgeStatus(data));
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const enforced = isEnabled('age_gate') && provisioned && Boolean(getSupabase());
  const canAccess = !enforced || canAccessGatedSurfaces(status);

  return { status, loading: loading || flagsLoading, enforced, canAccess };
};
